import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Search } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Logo } from "@/components/site/Logo";
import { BookingList } from "@/components/admin/BookingList";
import { BookingDetail } from "@/components/admin/BookingDetail";
import { ReviewsTab } from "@/components/admin/ReviewsTab";
import { EstimatesTab } from "@/components/admin/EstimatesTab";
import { STATUSES } from "@/components/admin/StatusBadge";
import { api, errMsg } from "@/lib/api";

const FILTERS = ["all", ...STATUSES];
const TABS = [["bookings", "Bookings"], ["reviews", "Reviews"], ["estimates", "Estimates"]];

export default function Admin() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [filter, setFilter] = useState("all");
  const [tab, setTab] = useState("bookings");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    try {
      const [b, rv, es] = await Promise.all([api.get("/admin/bookings"), api.get("/admin/reviews"), api.get("/admin/estimates")]);
      setItems(Array.isArray(b.data) ? b.data : []);
      setReviews(Array.isArray(rv.data) ? rv.data : []);
      setEstimates(Array.isArray(es.data) ? es.data : []);
    } catch (e) {
      if ([401, 403].includes(e?.response?.status)) nav("/admin/login");
      else toast.error(errMsg(e));
    }
  }, [nav]);

  useEffect(() => {
    if (!localStorage.getItem("caf_token")) { nav("/admin/login"); return; }
    load();
  }, [load, nav]);

  const counts = useMemo(() => Object.fromEntries(FILTERS.map((f) => [f, f === "all" ? items.length : items.filter((b) => b.status === f).length])), [items]);
  const shown = items.filter((b) => (filter === "all" || b.status === filter) &&
    `${b.name} ${b.email} ${b.reference} ${b.service_name}`.toLowerCase().includes(q.toLowerCase()));

  const setStatus = async (b, status) => {
    try {
      const { data } = await api.patch(`/admin/bookings/${b.id}`, { status });
      setItems((xs) => xs.map((x) => (x.id === b.id ? data : x)));
      setOpen(data);
      toast.success(`Marked ${status}${["confirmed", "cancelled"].includes(status) ? ` · email ${data.email_status}` : ""}`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (b) => {
    if (!window.confirm(`Delete booking ${b.reference}?`)) return;
    try {
      await api.delete(`/admin/bookings/${b.id}`);
      setItems((xs) => xs.filter((x) => x.id !== b.id));
      setOpen(null);
      toast.success("Booking deleted");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const approveReview = async (r, approved) => {
    try {
      const { data } = await api.patch(`/admin/reviews/${r.id}`, { approved });
      setReviews((xs) => xs.map((x) => (x.id === r.id ? data : x)));
      toast.success(approved ? "Review posted to the site" : "Review unposted");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const deleteReview = async (r) => {
    if (!window.confirm("Delete this review?")) return;
    try { await api.delete(`/admin/reviews/${r.id}`); setReviews((xs) => xs.filter((x) => x.id !== r.id)); toast.success("Review deleted"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const deleteEstimate = async (est) => {
    if (!window.confirm("Delete this estimate request?")) return;
    try { await api.delete(`/admin/estimates/${est.id}`); setEstimates((xs) => xs.filter((x) => x.id !== est.id)); toast.success("Estimate deleted"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const logout = () => { localStorage.removeItem("caf_token"); nav("/admin/login"); };

  return (
    <main className="min-h-screen bg-[#090A0C] text-zinc-100" data-testid="admin-dashboard">
      <header className="border-b border-white/10">
        <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between px-5 sm:px-8">
          <Link to="/" data-testid="admin-logo-link"><Logo /></Link>
          <button onClick={logout} className="inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-white" data-testid="admin-logout-button"><LogOut className="h-4 w-4" /> Log out</button>
        </div>
      </header>
      <div className="mx-auto max-w-[1400px] px-5 py-10 sm:px-8">
        <h1 className="font-display text-5xl uppercase leading-none sm:text-7xl">The <span className="font-serif-i normal-case text-acid">books.</span></h1>
        <div className="mt-10 flex flex-wrap gap-3" data-testid="admin-tabs">
          {TABS.map(([id, label]) => {
            const n = id === "bookings" ? items.length : id === "reviews" ? reviews.filter((r) => !r.approved).length : estimates.length;
            return (
              <button key={id} onClick={() => setTab(id)} data-testid={`admin-tab-${id}`}
                className={`border px-6 py-3.5 font-display text-2xl uppercase tracking-wide transition-colors duration-300 ${tab === id ? "border-[var(--acid)] bg-[#EDB89E]/10 text-zinc-50" : "border-white/10 bg-[#111317] text-zinc-300 hover:border-white/25"}`}>
                {label} <span className="ml-1 font-mono-x text-xs text-zinc-400">{n}</span>
              </button>
            );
          })}
        </div>
        {tab === "bookings" && (
          <>
            <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-5">
              {FILTERS.map((f) => (
                <button key={f} onClick={() => setFilter(f)} data-testid={`admin-filter-${f}`}
                  className={`border p-5 text-left transition-colors duration-300 ${filter === f ? "border-[var(--acid)] bg-[#EDB89E]/10" : "border-white/10 bg-[#111317] hover:border-white/25"}`}>
                  <span className="eyebrow">{f}</span>
                  <span className="mt-3 block font-display text-4xl text-zinc-50" data-testid={`admin-count-${f}`}>{counts[f]}</span>
                </button>
              ))}
            </div>
            <div className="relative mt-8 mb-5 max-w-md">
              <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, reference…" data-testid="admin-search-input"
                className="h-12 rounded-none border-white/15 bg-[#111317] pl-11 text-zinc-100 focus-visible:ring-[var(--acid)]" />
            </div>
            <BookingList items={shown} onOpen={setOpen} />
          </>
        )}
        {tab === "reviews" && <ReviewsTab items={reviews} onApprove={approveReview} onDelete={deleteReview} />}
        {tab === "estimates" && <EstimatesTab items={estimates} onDelete={deleteEstimate} />}
      </div>
      <BookingDetail booking={open} onClose={() => setOpen(null)} onStatus={setStatus} onDelete={remove} />
    </main>
  );
}
